import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Typography, { typographyClasses } from '@mui/material/Typography';
import { t } from 'i18next';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';
import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import { CompanyTypeahead } from '~hooks/useCompany/models';
import { useStores } from '~store';

import CompanyFoundCard from './CompanyFoundCard';
import SearchCompanyByTreadIdForm from './SearchCompanyByTreadIdForm';

const connectAccountByTreadIdSchema = yup.object().shape({
  senderCompany: yup
    .object()
    .shape({
      id: yup.string().trim().required(),
      legalName: yup.string().trim().required(),
    })
    .required(),
  treadId: yup.string().trim().required(),
  isCustomer: yup.boolean().required(),
  isVendor: yup.boolean().required(),
});

type ConnectAccountByTreadIdDto = yup.InferType<typeof connectAccountByTreadIdSchema>;

export type ConnectAccountByTreadIdFormHandler = {
  onSubmit: () => Promise<ConnectAccountByTreadIdDto | undefined>;
};

interface ConnectAccountByTreadIdFormProps {
  onFormValidityChange: (isFormValid: boolean) => void;
}

const ConnectAccountByTreadIdForm = forwardRef<
  ConnectAccountByTreadIdFormHandler,
  ConnectAccountByTreadIdFormProps
>(function ConnectAccountByTreadIdForm({ onFormValidityChange }, ref) {
  const { userStore } = useStores();

  const currentCompany = userStore.currentCompanies[0];
  const companyHasChildCompanies = userStore.userCompanies.length > 1;

  const [receiverCompany, setReceiverCompany] = useState<CompanyTypeahead | null>(null);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useForm<ConnectAccountByTreadIdDto>({
    resolver: yupResolver(connectAccountByTreadIdSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      isCustomer: false,
      isVendor: false,
      senderCompany: { id: currentCompany.id, legalName: currentCompany.legalName },
      treadId: '',
    },
  });

  const allFormValues = watch();

  const isFormValid = useMemo(() => {
    try {
      connectAccountByTreadIdSchema.validateSync(allFormValues);

      return allFormValues.isCustomer || allFormValues.isVendor;
    } catch {
      return false;
    }
  }, [allFormValues]);

  const handleSelectedCompanyChange = useCallback<
    React.ComponentProps<typeof SearchCompanyByTreadIdForm>['onSelectedCompanyChange']
  >((company, treadId) => {
    setReceiverCompany(company);
    setValue('treadId', treadId);
    setValue('isCustomer', false);
    setValue('isVendor', false);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      onSubmit() {
        return new Promise((resolve) => {
          handleSubmit(
            (data) => {
              resolve(data);
            },
            () => {
              resolve(undefined);
            },
          )();
        });
      },
    }),
    [handleSubmit],
  );

  useEffect(() => {
    onFormValidityChange(isFormValid);
  }, [isFormValid, onFormValidityChange]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  return (
    <Box>
      {companyHasChildCompanies && (
        <>
          <FormFieldLabel
            label={t('account.connect_with_tread_id_company_select')}
            variant="body1"
          />

          <CompanySelectorFormField
            control={control}
            errors={errors}
            hideLabel
            isRequired
            name="senderCompany"
            sx={{ width: '100%' }}
          />
        </>
      )}

      <Typography sx={{ mb: 0, mt: 4 }}>
        {t('account.connect_with_tread_id_instructions')}
      </Typography>

      <SearchCompanyByTreadIdForm onSelectedCompanyChange={handleSelectedCompanyChange} />

      {receiverCompany && (
        <>
          <CompanyFoundCard
            company={CompanyTypeahead.parse({
              id: receiverCompany.id,
              legal_name: receiverCompany.legalName,
            })}
            treadId={allFormValues.treadId}
            sx={{ mt: 3 }}
          />

          <Box
            component="form"
            data-test-id="search-company-by-tread-id-form"
            sx={{ mt: 3 }}
          >
            <FormFieldLabel
              label={t('account.choose_account_types_heading')}
              variant="body1"
              sx={{ flexShrink: 0 }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <CheckboxInput
                control={control}
                label={t('common.customer')}
                name="isCustomer"
                sx={{
                  width: 'auto',
                  [`& .${typographyClasses.root}`]: {
                    fontSize: '13px',
                    fontWeight: 400,
                  },
                }}
              />

              <CheckboxInput
                control={control}
                label={t('common.vendor')}
                name="isVendor"
                sx={{
                  width: 'auto',
                  [`& .${typographyClasses.root}`]: {
                    fontSize: '13px',
                    fontWeight: 400,
                  },
                }}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
});

export default ConnectAccountByTreadIdForm;
